// TES-2076 (D-UI): Namensauflösung für die "Aufgaben als Stellvertreter"-Sektion. Die Deputy-Union
// (my_deputy_tasks, Mig 387) liefert nur die IDs des vertretenen Prinzipals (principal_user_id für
// Ebene mpp/user, principal_group_id für Ebene group). Dieser Hook reichert sie — wie die Nachbar-Tabs
// (useUserDeputy nutzt list_tenant_profiles) — mit Anzeigenamen an. RLS-scoped: list_tenant_profiles
// ist tenant-scoped, team_group über die Gruppen-RLS. Reine Read-Anreicherung, KEINE Sicherheitsschicht.
//
// Die Logik ist bewusst in flache Helfer AUSSERHALB des Hooks zerlegt (Repo-Muster, vgl.
// resolveNotificationVisual TES-2028 / 84ea32d1): jede Funktion bleibt unter der fallow-Schwelle, sodass
// der Regression-Gate auch dann grün bleibt, wenn der CI-Coverage-Step timeoutet und fallow ohne
// Coverage scort (CRAP = CC²·(1−cov)³ + CC explodiert sonst für getesteten, aber verzweigten Code).
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { UnifiedTask, DeputyTaskData } from "@/hooks/useTasksQuery";

interface TenantProfileRow {
  user_id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

/** Bevorzugt display_name → "Vorname Nachname" → email → null (Muster useUserDeputy.profileName). */
function profileName(p: TenantProfileRow): string | null {
  const full = [p.first_name, p.last_name].filter(Boolean).join(" ");
  return p.display_name || full || p.email || null;
}

export interface DeputyPrincipalNames {
  /** user_id → Anzeigename (Ebene mpp/user). */
  userNames: Map<string, string>;
  /** group_id → Gruppenname (Ebene group). */
  groupNames: Map<string, string>;
  isLoading: boolean;
}

interface PrincipalIds {
  userIds: string[];
  groupIds: string[];
}

/** Deputy-Nutzdaten einer Task (nur type === "deputy" mit deputyData), sonst null. */
function deputyDataOf(task: UnifiedTask): DeputyTaskData | null {
  if (task.type !== "deputy") return null;
  return task.deputyData ?? null;
}

/** Sammelt die Prinzipal-IDs EINER Task in die geteilten Sets (User/Gruppe). */
function addPrincipalIds(task: UnifiedTask, userIds: Set<string>, groupIds: Set<string>): void {
  const d = deputyDataOf(task);
  if (!d) return;
  if (d.principal_user_id) userIds.add(d.principal_user_id);
  if (d.principal_group_id) groupIds.add(d.principal_group_id);
}

/** Sammelt die eindeutigen Prinzipal-IDs aus den Deputy-Tasks (nur type === "deputy"). */
function collectPrincipalIds(tasks: UnifiedTask[]): PrincipalIds {
  const userIds = new Set<string>();
  const groupIds = new Set<string>();
  for (const t of tasks) addPrincipalIds(t, userIds, groupIds);
  return { userIds: [...userIds], groupIds: [...groupIds] };
}

/** Reduziert die Profile-Zeilen auf die gewünschten user_ids → Anzeigename (leere Namen fallen raus). */
function pickWantedNames(rows: TenantProfileRow[], wanted: Set<string>): Map<string, string> {
  const names = new Map<string, string>();
  for (const p of rows) {
    if (!wanted.has(p.user_id)) continue;
    const name = profileName(p);
    if (name) names.set(p.user_id, name);
  }
  return names;
}

/** Löst User-Prinzipale via list_tenant_profiles auf (leere Menge ⇒ kein RPC-Call). */
async function fetchUserNames(userIds: string[]): Promise<Map<string, string>> {
  if (userIds.length === 0) return new Map();
  const { data, error } = await supabase.rpc("list_tenant_profiles");
  if (error) throw error;
  return pickWantedNames((data as TenantProfileRow[] | null) ?? [], new Set(userIds));
}

/** id → name aus den team_group-Zeilen. */
function toNameMap(rows: Array<{ id: string; name: string }>): Map<string, string> {
  const map = new Map<string, string>();
  for (const g of rows) map.set(g.id, g.name);
  return map;
}

/** Löst Gruppen-Prinzipale via team_group auf (leere Menge ⇒ kein Select-Call). */
async function fetchGroupNames(groupIds: string[]): Promise<Map<string, string>> {
  if (groupIds.length === 0) return new Map();
  const { data, error } = await supabase.from("team_group").select("id, name").in("id", groupIds);
  if (error) throw error;
  return toNameMap((data as Array<{ id: string; name: string }> | null) ?? []);
}

/** Stabiler Auth-User-Key ("" wenn ausgeloggt) — hält den ?.-/??-Zweig aus dem Hook. */
function userIdOf(user: { id: string } | null | undefined): string {
  return user?.id ?? "";
}

/** Gibt es überhaupt einen aufzulösenden Prinzipal? (steuert den enabled-Guard). */
function hasAnyPrincipal(ids: PrincipalIds): boolean {
  return ids.userIds.length > 0 || ids.groupIds.length > 0;
}

/** Stabiler Query-Key aus den sortierten IDs — ändert sich nur mit der aufzulösenden Menge. */
function deputyPrincipalsKey(
  userId: string,
  userIds: string[],
  groupIds: string[],
): readonly unknown[] {
  return [
    "deputy-principals",
    userId,
    [...userIds].sort().join(","),
    [...groupIds].sort().join(","),
  ];
}

interface DeputyQueryData {
  userNames: Map<string, string>;
  groupNames: Map<string, string>;
}

/** Query-Resultat → öffentliche Hook-Form (leere Maps solange keine Daten). */
function mapDeputyResult(
  data: DeputyQueryData | undefined,
  isLoading: boolean,
): DeputyPrincipalNames {
  const resolved = data ?? {
    userNames: new Map<string, string>(),
    groupNames: new Map<string, string>(),
  };
  return { userNames: resolved.userNames, groupNames: resolved.groupNames, isLoading };
}

/**
 * useDeputyPrincipals (TES-2076) — löst die vertretenen Prinzipale (User + Gruppe) der Deputy-Tasks
 * in Anzeigenamen auf. Enabled-Guard: läuft nur, wenn der Caller eingeloggt ist UND es mindestens
 * einen Deputy-Task mit aufzulösendem Prinzipal gibt (kein Leerlauf-Fetch).
 */
export function useDeputyPrincipals(tasks: UnifiedTask[]): DeputyPrincipalNames {
  const { user } = useAuth();
  const { userIds, groupIds } = collectPrincipalIds(tasks);
  const hasPrincipals = hasAnyPrincipal({ userIds, groupIds });
  const userId = userIdOf(user);

  const query = useQuery({
    queryKey: deputyPrincipalsKey(userId, userIds, groupIds),
    enabled: !!userId && hasPrincipals,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<DeputyQueryData> => {
      // Sequentiell (Verhalten wie zuvor): erst User- dann Gruppen-Auflösung, jede RLS-scoped.
      const userNames = await fetchUserNames(userIds);
      const groupNames = await fetchGroupNames(groupIds);
      return { userNames, groupNames };
    },
  });

  return mapDeputyResult(query.data, query.isLoading && hasPrincipals);
}
